import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronRight, CheckCircle2, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { programmaRegionale } from '../data/siteData';

export const ProgrammaRegionale = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="programma-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-[#002060] to-[#003380] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna alla home
          </Link>
          
          <div className="text-center">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#5CB9E5] mb-4 block">
              Elezioni Regionali 2025
            </span>
            <h1 className="font-serif text-4xl md:text-6xl font-bold text-white mb-4">
              Programma Elettorale
            </h1>
            <p className="text-xl text-[#FFD700] font-medium mb-4">
              Lista INSIEME - ENSEMBLE
            </p>
            <p className="text-white/80 max-w-3xl mx-auto">
              Il progetto Insieme-Ensemble tra Forza Italia e La Renaissance Valdôtaine rappresenta 
              il centro moderato della coalizione di centrodestra e si pone come forza di rinnovamento 
              e rilancio per la regione Valle d'Aosta.
            </p>
          </div>
        </div>
      </section>

      {/* First 90 Days */}
      <section className="py-12 bg-[#FFD700]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#002060] text-center mb-8">
            Cosa faremo nei primi 90 giorni di legislatura
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              "Riforma elettorale con elezione diretta del Presidente",
              "Abbattimento costi energia elettrica per prima casa",
              "Raddoppio linea ferroviaria Aosta-Ivrea",
              "Abbattimento liste d'attesa in sanità",
              "Legge per il Terzo Settore",
              "Ordinamento Vigili del Fuoco e Corpo Forestale"
            ].map((item) => (
              <div 
                key={item}
                className="flex items-start gap-3 bg-white/20 rounded-lg p-4"
              >
                <CheckCircle2 className="w-5 h-5 text-[#002060] flex-shrink-0 mt-0.5" />
                <span className="text-[#002060] font-medium text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programma Accordion */}
      <section className="py-16 bg-white" data-testid="programma-accordion">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#002060] mb-4">
              I 16 punti del programma
            </h2>
            <p className="text-slate-600">
              Clicca su ogni sezione per approfondire le nostre proposte
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-3">
            {programmaRegionale.map((punto) => (
              <AccordionItem 
                key={punto.id} 
                value={`item-${punto.id}`}
                className="bg-[#F4F6F8] rounded-xl border-none overflow-hidden"
              >
                <AccordionTrigger className="px-6 py-5 hover:no-underline hover:bg-[#002060]/5 [&[data-state=open]]:bg-[#002060] [&[data-state=open]]:text-white transition-colors">
                  <div className="flex items-center gap-4">
                    <span className="text-[#5CB9E5] font-bold text-lg [&[data-state=open]]:text-[#FFD700]">
                      {String(punto.id).padStart(2, '0')}
                    </span>
                    <span className="font-serif text-lg font-semibold text-left">
                      {punto.titolo}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 py-6 bg-white">
                  <ul className="space-y-3">
                    {punto.punti.map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-[#5CB9E5] flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Navigation Cards */}
      <section className="py-16 bg-[#F4F6F8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#002060] text-center mb-12">
            Esplora le altre sezioni
          </h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link 
              to="/elezioni-regionali"
              className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow border-t-4 border-[#FFD700] group"
            >
              <h3 className="font-serif text-lg font-bold text-[#002060] mb-2 group-hover:text-[#5CB9E5] transition-colors">
                Candidati Regionali
              </h3>
              <p className="text-slate-600 text-sm mb-4">
                Scopri i 35 candidati della lista
              </p>
              <span className="text-[#5CB9E5] text-sm font-medium flex items-center gap-1">
                Scopri <ChevronRight className="w-4 h-4" />
              </span>
            </Link>

            <Link 
              to="/elezioni-comunali"
              className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow border-t-4 border-[#5CB9E5] group"
            >
              <h3 className="font-serif text-lg font-bold text-[#002060] mb-2 group-hover:text-[#5CB9E5] transition-colors">
                Candidati Comunali
              </h3>
              <p className="text-slate-600 text-sm mb-4">
                I candidati per Aosta
              </p>
              <span className="text-[#5CB9E5] text-sm font-medium flex items-center gap-1">
                Scopri <ChevronRight className="w-4 h-4" />
              </span>
            </Link>

            <Link 
              to="/valori"
              className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow border-t-4 border-[#002060] group"
            >
              <h3 className="font-serif text-lg font-bold text-[#002060] mb-2 group-hover:text-[#5CB9E5] transition-colors">
                I Nostri Valori
              </h3>
              <p className="text-slate-600 text-sm mb-4">
                I principi che ci guidano
              </p>
              <span className="text-[#5CB9E5] text-sm font-medium flex items-center gap-1">
                Scopri <ChevronRight className="w-4 h-4" />
              </span>
            </Link>

            <Link 
              to="/elezioni-trasparenti"
              className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow border-t-4 border-[#FFD700] group"
            >
              <h3 className="font-serif text-lg font-bold text-[#002060] mb-2 group-hover:text-[#5CB9E5] transition-colors">
                Trasparenza
              </h3>
              <p className="text-slate-600 text-sm mb-4">
                CV e certificati candidati
              </p>
              <span className="text-[#5CB9E5] text-sm font-medium flex items-center gap-1">
                Scopri <ChevronRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#002060]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-3xl font-bold text-white mb-4">
            Vuoi contribuire al cambiamento?
          </h2>
          <p className="text-white/80 mb-8">
            Entra nella squadra e aiutaci a costruire il futuro della Valle d'Aosta
          </p>
          <Link to="/unisciti">
            <Button className="btn-gold px-8 py-3 rounded-lg font-semibold flex items-center gap-2 mx-auto">
              Unisciti a noi
              <ChevronRight className="w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
